import {MELEE_ANIMATION} from '../../configs/meleeAnimation';

/**
 * Расписание ударов ближнего боя и геометрия одного удара (см.
 * configs/meleeAnimation.js, HexGridRenderer.showMeleeAnimation). Чистые
 * функции: что, когда и где рисовать — без Phaser.
 */

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const randomRange = (rng, range) => range.min + (range.max - range.min) * rng();

/**
 * Удары боя: по одному на каждую цель, которой отряд нанес урон. В записи
 * pendingMeleeDamage цели перечислены все, в том числе с нулевым уроном
 * (урон кончился или цель добили другие) — по таким удара нет.
 *
 * Порядок целей одного отряда — как в записи: приоритетная первой, дальше по
 * возрастанию размера (так урон распределяет редьюсер).
 *
 * @param {Array} pendingMeleeDamage
 * @param {Object} [options]
 * @param {string[]} [options.attackerUnitIds] — только удары этих отрядов
 *   (повтор после выбора приоритетной цели или переброса костей)
 * @returns {Array<{attackerUnitId: string, attackerOwnerId: string, targetUnitId: string, damage: number}>}
 */
export const collectMeleeHits = (pendingMeleeDamage, {attackerUnitIds} = {}) => (
  (pendingMeleeDamage || [])
    .filter((record) => record && (!attackerUnitIds || attackerUnitIds.includes(record.unitId)))
    .flatMap((record) => Object.entries(record.damageDistribution || {})
      .filter(([, damage]) => damage > 0)
      .map(([targetUnitId, damage]) => ({
        attackerUnitId: record.unitId,
        attackerOwnerId: record.ownerId,
        targetUnitId,
        damage,
      })))
);

/**
 * Расписание ударов. Сначала бьют отряды игрока, чей сейчас ход, после паузы —
 * отряды остальных. Внутри стороны — волнами: в первой волне каждый отряд
 * бьет свою первую цель, во второй — вторую и так далее; волны идут одна за
 * другой.
 *
 * @param {Object} params
 * @param {Array} params.hits — удары (см. collectMeleeHits)
 * @param {string} [params.firstOwnerId] — игрок, чей сейчас ход
 * @param {boolean} [params.isCollapsed] — все цели отряда бьются одной волной
 *   (чужой бой, когда волны по очереди не укладываются в шаг темпа)
 * @returns {{strikes: Object[], totalMs: number, revealAtMs: number}}
 *   strikes — удары с side (0 — ходящий игрок, 1 — остальные), wave, startAtMs,
 *   impactAtMs и isLungeLeader (отряд делает рывок к этой цели; при одной
 *   волне на все цели — только к первой). revealAtMs — когда показывать итог
 *   боя на плашках, totalMs — когда отыграл последний удар
 */
export const buildMeleeTimeline = ({hits, firstOwnerId, isCollapsed = false} = {}) => {
  const {strike: strikeConfig, sideGapMs, revealAfterLastWaveMs} = MELEE_ANIMATION;
  const impactOffsetMs = strikeConfig.slash.startAtMs + strikeConfig.slash.durationMs / 2;
  const sides = [
    (hits || []).filter((hit) => hit.attackerOwnerId === firstOwnerId),
    (hits || []).filter((hit) => hit.attackerOwnerId !== firstOwnerId),
  ];

  const strikes = [];
  let cursorMs = 0;
  let lastWaveStartMs = 0;
  sides.forEach((sideHits, side) => {
    if (sideHits.length === 0) {
      return;
    }
    if (strikes.length > 0) {
      cursorMs += sideGapMs;
    }
    const strikesByAttacker = new Map();
    let waveCount = 0;
    sideHits.forEach((hit) => {
      const order = strikesByAttacker.get(hit.attackerUnitId) || 0;
      strikesByAttacker.set(hit.attackerUnitId, order + 1);
      const wave = isCollapsed ? 0 : order;
      waveCount = Math.max(waveCount, wave + 1);
      const startAtMs = cursorMs + wave * strikeConfig.durationMs;
      strikes.push({
        ...hit,
        side,
        wave,
        startAtMs,
        impactAtMs: startAtMs + impactOffsetMs,
        isLungeLeader: order === 0 || !isCollapsed,
        // Взмахи отряда чередуют сторону: два удара подряд не повторяют друг друга
        isMirrored: order % 2 === 1,
      });
    });
    lastWaveStartMs = cursorMs + (waveCount - 1) * strikeConfig.durationMs;
    cursorMs += waveCount * strikeConfig.durationMs;
  });

  if (strikes.length === 0) {
    return {strikes, totalMs: 0, revealAtMs: 0};
  }
  return {
    strikes,
    totalMs: cursorMs,
    revealAtMs: Math.min(cursorMs, lastWaveStartMs + revealAfterLastWaveMs),
  };
};

/**
 * Во сколько раз сжать удары по времени, чтобы итог боя появился до следующего
 * действия в чужом темпе и успел побыть на поле (см. MELEE_ANIMATION.pace).
 * Свой бой идет в темпе самого игрока — не сжимается.
 *
 * @param {Object} params
 * @param {number} params.revealAtMs — когда итог появляется при обычной скорости
 * @param {number} [params.paceMs] — шаг темпа; не задан — темп свой
 * @param {number} [params.delayMs] — пауза до первого удара (переброс костей):
 *   она не сжимается и съедает часть шага
 * @returns {number} множитель длительностей: 1 — как есть, 0 — в шаг не
 *   уложиться даже сжатыми ударами
 */
export const resolveMeleeTimeFactor = ({revealAtMs, paceMs, delayMs = 0} = {}) => {
  if (!Number.isFinite(paceMs) || paceMs <= 0 || !(revealAtMs > 0)) {
    return 1;
  }

  const {resultHoldShare, minTimeFactor} = MELEE_ANIMATION.pace;
  const timeFactor = Math.min(1, (paceMs * (1 - resultHoldShare) - delayMs) / revealAtMs);
  return timeFactor < minTimeFactor ? 0 : timeFactor;
};

/**
 * Расписание, уложенное в шаг темпа. Сначала — волны по очереди; не
 * укладываются даже сжатыми — все цели отряда одной волной; не помогло и это
 * — анимации нет.
 *
 * @param {Object} params
 * @param {Array} params.hits
 * @param {string} [params.firstOwnerId]
 * @param {number} [params.paceMs] — шаг темпа чужих действий; не задан — свой бой
 * @param {number} [params.delayMs] — пауза до первого удара
 * @returns {{strikes, totalMs, revealAtMs, timeFactor, isCollapsed}|null}
 */
export const planMeleeTimeline = ({hits, firstOwnerId, paceMs, delayMs = 0} = {}) => {
  for (const isCollapsed of [false, true]) {
    const timeline = buildMeleeTimeline({hits, firstOwnerId, isCollapsed});
    if (timeline.strikes.length === 0) {
      return null;
    }
    const timeFactor = resolveMeleeTimeFactor({revealAtMs: timeline.revealAtMs, paceMs, delayMs});
    if (timeFactor > 0) {
      return {...timeline, timeFactor, isCollapsed};
    }
  }
  return null;
};

/**
 * Какое «−N» всплывает над целью от каждого удара: потеря численности, которую
 * добавил именно этот удар. На плашке отряда после боя стоит потеря по сумме
 * всех ударов, и она не всегда равна урону: у орков и у отряда в крепости счет
 * свой, а больше своего размера отряд не теряет. Числа ударов в сумме дают
 * число на плашке; удар, не убавивший численность (поглотила крепость, цель
 * уже добита), числа не показывает.
 *
 * @param {Array} strikes — удары расписания (порядок — по времени)
 * @param {Object} params
 * @param {Function} params.resolveSizeLoss — (targetUnitId, урон) => потеря
 *   численности при таком суммарном уроне по цели
 * @param {Object} [params.baseDamageByTarget] — урон по цели от отрядов, чьих
 *   ударов в расписании нет (повтор ударов одного отряда: остальные уже били)
 * @returns {Array} удары с полем sizeLoss
 */
export const assignMeleeStrikeLosses = (strikes, {resolveSizeLoss, baseDamageByTarget = {}} = {}) => {
  const damageByTarget = new Map();
  return [...strikes]
    .map((strike, index) => ({strike, index}))
    .sort((a, b) => a.strike.startAtMs - b.strike.startAtMs || a.index - b.index)
    .map(({strike, index}) => {
      const before = damageByTarget.get(strike.targetUnitId) ?? (baseDamageByTarget[strike.targetUnitId] || 0);
      const after = before + strike.damage;
      damageByTarget.set(strike.targetUnitId, after);
      const sizeLoss = Math.max(0, resolveSizeLoss(strike.targetUnitId, after) - resolveSizeLoss(strike.targetUnitId, before));
      return {index, strike: {...strike, sizeLoss}};
    })
    .sort((a, b) => a.index - b.index)
    .map(({strike}) => strike);
};

/**
 * Геометрия удара: рывок бьющего отряда к цели и дуга взмаха. Оружие идет
 * вокруг отряда (после рывка), рукоятью к нему и острием наружу; середина
 * дуги смотрит на цель — в этот момент удар и попадает.
 *
 * @param {Object} params
 * @param {{x: number, y: number}} params.from — бьющий отряд
 * @param {{x: number, y: number}} params.to — цель
 * @param {number} params.hexSize
 * @param {boolean} [params.isMirrored] — взмах в обратную сторону
 * @returns {Object} direction — угол на цель; lunge — смещение рывка; pivot —
 *   центр дуги; handleRadius — радиус, по которому идет рукоять; weaponLength;
 *   trailRadius и trailWidth — световой след; startAngle и endAngle — дуга
 */
export const buildMeleeStrikeGeometry = ({from, to, hexSize, isMirrored = false} = {}) => {
  const {lunge: lungeConfig, slash} = MELEE_ANIMATION.strike;
  const deltaX = to.x - from.x;
  const deltaY = to.y - from.y;
  const distance = Math.hypot(deltaX, deltaY);
  // Цель на той же точке (быть не должно) — бьем вправо, а не делим на ноль
  const direction = distance > 0 ? Math.atan2(deltaY, deltaX) : 0;
  const lungeDistance = Math.min(hexSize * lungeConfig.distanceFactor, distance * 0.4);
  const lunge = {x: Math.cos(direction) * lungeDistance, y: Math.sin(direction) * lungeDistance};
  const pivot = {x: from.x + lunge.x, y: from.y + lunge.y};

  const weaponLength = hexSize * slash.weaponLengthFactor;
  // Острие проходит эту долю пути от бьющего (после рывка) до цели
  const reach = Math.max(weaponLength, (distance - lungeDistance) * slash.reachShare);
  const handleRadius = Math.max(hexSize * 0.12, reach - weaponLength);
  const halfSweep = (slash.sweepDeg / 2) * Math.PI / 180 * (isMirrored ? -1 : 1);

  return {
    direction,
    distance,
    lunge,
    pivot,
    weaponLength,
    handleRadius,
    // След идет по дальней части клинка
    trailRadius: handleRadius + weaponLength * 0.68,
    trailWidth: hexSize * slash.trailWidthFactor,
    startAngle: direction - halfSweep,
    endAngle: direction + halfSweep,
    impact: {x: to.x, y: to.y},
    hexSize,
  };
};

// Плавный разгон и торможение: быстрее всего оружие идет в середине дуги — в
// момент попадания
const easeSwing = (value) => value * value * (3 - 2 * value);

/**
 * Положение оружия и следа в заданный момент взмаха.
 *
 * @param {Object} geometry — см. buildMeleeStrikeGeometry
 * @param {number} progress — ход взмаха: 0 — замах, 0.5 — попадание, 1 — конец
 * @returns {{angle, sweptShare, x, y, rotation}} x, y — рукоять; rotation —
 *   поворот спрайта, чей клинок в текстуре смотрит вверх
 */
export const resolveMeleeSlashState = (geometry, progress) => {
  const t = easeSwing(clamp01(progress));
  const angle = geometry.startAngle + (geometry.endAngle - geometry.startAngle) * t;

  return {
    angle,
    // Пройденная доля дуги: по ней растет световой след
    sweptShare: t,
    x: geometry.pivot.x + Math.cos(angle) * geometry.handleRadius,
    y: geometry.pivot.y + Math.sin(angle) * geometry.handleRadius,
    rotation: angle + Math.PI / 2,
  };
};
